<template>
  <div>
    <kudo-form-control
      v-if="!manualAddressMode"
      class="col-span-12 relative"
      :errors="r$.$fields.propertyAddress.$errors"
    >
      <kudo-input
        v-model="r$.$value.propertyAddress"
        :aria-invalid="r$.$fields.propertyAddress.$errors.length"
        :label-text="props.label"
        required
        autocomplete="off"
        data-cypress="address-autocomplete-input"
        @blur="(e) => handleAddressBlur(e)"
        @update:model-value="(val) => fetchSuggestions(val)"
      >
        <template #label-utility>
          <slot name="label-utility" />
          <kudo-button
            v-if="props.showClearAddress"
            class="ml-auto"
            color-variant="plain"
            shape-variant="thin"
            @click="clearPropertyAddress"
          >
            Clear
          </kudo-button>
        </template>
        <template #start-icon>
          <i class="fas fa-search text-xs" />
        </template>
        <template #end-icon>
          <i
            v-if="fetchingAutompleteResults"
            class="fas fa-spinner-third fa-spin text-xs"
          />
        </template>
      </kudo-input>
      <kudo-card
        v-show="shouldShowSuggestions"
        class="absolute z-10 w-full top-[68px]"
      >
        <ul
          ref="results"
          tabindex="0"
        />
      </kudo-card>
    </kudo-form-control>
    <div
      v-else
      class="grid grid-cols-12 gap-y-2 gap-x-4"
    >
      <kudo-form-control
        class="col-span-12 md:col-span-3"
        :errors="r$.$fields.streetNumber.$errors"
      >
        <kudo-input
          v-model="r$.$value.streetNumber"
          :aria-invalid="r$.$fields.streetNumber.$errors.length"
          label-text="Street Number"
          required
          data-cypress="address-autocomplete-street-number"
          @update:model-value="handleAddressFieldChanges"
        />
      </kudo-form-control>
      <kudo-form-control
        class="col-span-12 md:col-span-9"
        :errors="r$.$fields.streetName.$errors"
      >
        <kudo-input
          v-model="r$.$value.streetName"
          :aria-invalid="r$.$fields.streetName.$errors.length"
          label-text="Street Name"
          data-cypress="address-autocomplete-street-name"
          required
          @update:model-value="handleAddressFieldChanges"
        />
      </kudo-form-control>
      <kudo-form-control
        class="col-span-12 md:col-span-6"
        :errors="r$.$fields.city.$errors"
      >
        <kudo-input
          v-model="r$.$value.city"
          :aria-invalid="r$.$fields.city.$errors.length"
          label-text="City"
          data-cypress="address-autocomplete-city"
          required
          @update:model-value="handleAddressFieldChanges"
        />
      </kudo-form-control>
      <kudo-form-control
        class="col-span-12 md:col-span-3"
        :errors="r$.$fields.state.$errors"
      >
        <kudo-select
          v-model="r$.$value.state"
          :aria-invalid="r$.$fields.state.$errors.length"
          :options="GeoStatesKudoOptions"
          is-searchable
          placeholder="Select"
          label-text="State"
          data-cypress="address-autocomplete-state"
          required
          @update:model-value="handleAddressFieldChanges"
        />
      </kudo-form-control>
      <kudo-form-control
        class="col-span-12 md:col-span-3"
        :errors="r$.$fields.zip.$errors"
      >
        <kudo-input
          v-model="r$.$value.zip"
          :aria-invalid="r$.$fields.zip.$errors.length"
          v-bind="$kudoMasks.zip"
          placeholder="XXXXX"
          data-cypress="address-autocomplete-zip"
          label-text="Zipcode"
          required
          @update:model-value="handleAddressFieldChanges"
        />
      </kudo-form-control>
    </div>
    <button
      class="text-plum-dark underline font-semibold text-sm col-span-12 text-left relative -top-2 w-auto self-start"
      type="button"
      data-cypress="address-autocomplete-manual-input-toggle"
      @click="toggleAddressMode"
    >
      {{ toggleLinkText }}
    </button>
  </div>
</template>

<script setup>
import { useScopedRegle } from '@regle/core'
import { KudoInput, KudoFormControl, KudoButton, KudoSelect, KudoCard } from '@openly-engineering/kudo'
import { Loader } from '@googlemaps/js-api-loader'
import { requiredIf, withMessage } from '@regle/rules'
import { GeoStatesKudoOptions } from '~/shared/geoStates'

const props = defineProps({
  label: {
    type: String,
    default: 'Property Address',
  },
  addressFields: {
    type: Object,
    default: null,
    validator: (address) => {
      if (address !== null) {
        const requiredKeys = ['streetNumber', 'streetName', 'city', 'state', 'zip']
        return requiredKeys.every(key => Object.prototype.hasOwnProperty.call(address, key))
      }
      // If prop is null, return it as valid by default
      return true
    },
  },
  showClearAddress: {
    type: Boolean,
    default: true,
  },
})

const results = ref(null)
const { $config } = useNuxtApp()
// address-invalidated: fires when the user clears or edits a previously valid address
const emit = defineEmits(['google-places-ready', 'address-updated', 'address-invalidated'])
const manualAddressMode = ref(false)
const shouldShowSuggestions = ref(false)
const fetchingAutompleteResults = ref(false)
// An address passed in via props is already validated, so treat it as if it were selected from the list.
// This requirement only re-applies once the user interacts with the field (see fetchSuggestions).
const addressSelectedFromList = ref(Boolean(props.addressFields))
let sessionToken = ''

const loader = new Loader({
  apiKey: $config.public.googlePlacesApiKey,
  version: 'weekly',
  libraries: ['places'],
})

loader.load().then(() => {
  emit('google-places-ready')
})

// This computed property is used to determine if the user must select an address from the list in the Regle validation
const mustSelectAddressFromList = computed(() => !manualAddressMode.value && !addressSelectedFromList.value && r$.$value.propertyAddress !== '')

const formFields = {
  propertyAddress: props.addressFields
    ? `${props.addressFields.streetNumber} ${props.addressFields.streetName}${props.addressFields.unit ? ' ' + props.addressFields.unit + ',' : ','} ${props.addressFields.city}, ${props.addressFields.state} ${props.addressFields.zip}`
    : '',
  streetNumber: props.addressFields ? props.addressFields.streetNumber : '',
  streetName: props.addressFields ? props.addressFields.streetName : '',
  city: props.addressFields ? props.addressFields.city : '',
  state: props.addressFields ? props.addressFields.state : '',
  zip: props.addressFields ? props.addressFields.zip : '',
}

const { r$ } = useScopedRegle(formFields, {
  propertyAddress: {
    required: requiredIf(() => !manualAddressMode.value),
    selectedFromAddressList: withMessage(() => !mustSelectAddressFromList.value, 'Please select an address from the search results'),
    $silent: true,
  },
  streetNumber: { required: requiredIf(manualAddressMode) },
  streetName: { required: requiredIf(manualAddressMode) },
  city: { required: requiredIf(manualAddressMode) },
  state: { required: requiredIf(manualAddressMode) },
  zip: { required: requiredIf(manualAddressMode) },
})

const toggleLinkText = computed(() => manualAddressMode.value ? 'Search address instead' : 'Can’t find the address?')

async function fetchSuggestions(searchText) {
  if (!searchText) {
    // don't attempt to show suggestions if the search field is empty
    if (addressSelectedFromList.value) {
      emit('address-invalidated')
    }
    addressSelectedFromList.value = false
    shouldShowSuggestions.value = false
    return
  }

  const { AutocompleteSessionToken, AutocompleteSuggestion } = await google.maps.importLibrary('places')

  if (!sessionToken) {
    // Create a session token and add it to the request for billing purposes
    // see `sessionToken` at https://developers.google.com/maps/documentation/javascript/reference/autocomplete-data
    sessionToken = await new AutocompleteSessionToken()
  }

  // Update refs
  fetchingAutompleteResults.value = true
  if (addressSelectedFromList.value) {
    emit('address-invalidated')
  }
  addressSelectedFromList.value = false

  const request = {
    input: searchText,
    includedPrimaryTypes: ['street_address', 'premise', 'subpremise'],
    includedRegionCodes: ['us'],
    language: 'en-US',
    region: 'us',
    sessionToken,
  }

  try {
    // Fetch autocomplete suggestions
    const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request)

    // Update refs for template purposes
    shouldShowSuggestions.value = true
    fetchingAutompleteResults.value = false

    const resultsContainerElement = results.value
    resultsContainerElement.replaceChildren()

    for (const suggestion of suggestions) {
      // Using Object.assign here for readability of created DOM elements and their attributes
      const suggestionListItem = Object.assign(document.createElement('li'), {
        className: 'autocomplete__option',
        innerText: suggestion.placePrediction.text.toString(),
        onclick: () => onPlaceSelected(suggestion.placePrediction.toPlace()),
      })

      suggestionListItem.prepend(Object.assign(document.createElement('i'), {
        className: 'fas fa-location-dot autocomplete__address-icon',
      }))

      resultsContainerElement.appendChild(suggestionListItem)
    }
  } catch (error) {
    // Reset UI state so the loading spinner and suggestions list don't get stuck on a failed request
    shouldShowSuggestions.value = false
    fetchingAutompleteResults.value = false
    // @todo: what do we want to do here? Check with product,
    // perhaps show an Error that autocomplete is not working and to use full address mode?
    // This can probably be a later ticket
    console.log(error)
  }
}

async function onPlaceSelected(place) {
  // According to Google's billing docs:
  // "The session begins when the user starts typing a query, and concludes when they select a place and call Place.fetchFields."
  sessionToken = ''
  addressSelectedFromList.value = true
  shouldShowSuggestions.value = false

  await place.fetchFields({
    fields: ['formattedAddress', 'addressComponents'],
  })

  r$.$value.propertyAddress = place.formattedAddress

  if (place.addressComponents) {
    let unit = ''
    // locality takes highest precedence; administrative_area_level_3 handles townships
    // (e.g. "Upper Darby Township") that have no locality. Priority tracked explicitly
    // because the API does not guarantee component order.
    const cityPriority = { neighborhood: 1, sublocality: 2, administrative_area_level_3: 3, locality: 4 }
    let cityCandidate = { priority: 0, value: '' }

    place.addressComponents.forEach((component) => {
      const { types, longText, shortText } = component

      // Account for Unit, Apartment, Suite, etc.
      if (types.includes('subpremise')) {
        unit = ` ${longText}`
      }

      if (types.includes('street_number')) {
        r$.$value.streetNumber = longText
      }
      if (types.includes('route')) {
        r$.$value.streetName = longText + unit
      }

      for (const [type, priority] of Object.entries(cityPriority)) {
        if (types.includes(type) && priority > cityCandidate.priority) {
          cityCandidate = { priority, value: longText }
        }
      }
      if (types.includes('administrative_area_level_1')) {
        r$.$value.state = shortText
      }
      if (types.includes('postal_code')) {
        r$.$value.zip = longText
      }
    })

    if (cityCandidate.value) {
      r$.$value.city = cityCandidate.value
    }
  }

  emit('address-updated', r$.$value)

  // We need to manually 'touch' the address field as it is default set to silent in Regle
  r$.$fields.propertyAddress.$touch()
}

function clearPropertyAddress() {
  shouldShowSuggestions.value = false
  addressSelectedFromList.value = false
  r$.$value.propertyAddress = ''
  emit('address-invalidated')
}

function handleAddressBlur(event) {
  const resultsList = results.value

  // We don't want to run this blur handler if the user is clicking on an address suggestion
  const related = event.relatedTarget
  const clickedInsideResults = resultsList && related && resultsList.contains(related)
  if (!clickedInsideResults) {
    shouldShowSuggestions.value = false
    r$.$fields.propertyAddress.$touch()
  }
}

function handleAddressFieldChanges() {
  clearPropertyAddress()
  emit('address-updated', r$.$value)
}

function toggleAddressMode() {
  manualAddressMode.value = !manualAddressMode.value
  r$.$reset()
}

</script>

<style>

.autocomplete__option {
  @apply text-gray-darker p-3 min-h-4 cursor-pointer text-sm leading-[.75];
}

.autocomplete__option:hover {
  @apply bg-plum-lightest text-gray-darkest;
}

.autocomplete__option:hover .autocomplete__address-icon {
  @apply text-plum-darkest;
}

.autocomplete__address-icon {
  @apply mr-2 text-gray;
}

</style>
